import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertTriangle } from 'lucide-react';

const AdminStatusChangeDialog = ({ open, onOpenChange, invoice, onConfirm }) => {
  const { t } = useTranslation();
  const [newStatus, setNewStatus] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newStatus || !reason.trim()) {
      return;
    }

    setLoading(true);
    try {
      await onConfirm({
        status: newStatus,
        status_change_reason: reason
      });
      setNewStatus('');
      setReason('');
      onOpenChange(false);
    } catch (error) {
      console.error('Error changing status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNewStatus('');
    setReason('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="rounded-sm sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            {t('invoices.adminStatusChange')}
          </DialogTitle>
          <DialogDescription>
            {t('invoices.adminStatusChangeDescription', { number: invoice?.invoice_number })}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Warning */}
          <div className="p-3 bg-amber-50 rounded-sm border border-amber-200">
            <p className="text-sm text-amber-800">
              {t('invoices.adminStatusChangeWarning')}
            </p>
          </div>

          {/* New Status */}
          <div className="space-y-2">
            <Label>{t('invoices.newStatus')} *</Label>
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger className="rounded-sm">
                <SelectValue placeholder={t('invoices.selectStatus')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">{t('invoices.statusDraft')}</SelectItem>
                <SelectItem value="sent">{t('invoices.statusSent')}</SelectItem>
                <SelectItem value="overdue">{t('invoices.statusOverdue')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">{t('invoices.statusChangeReason')} *</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              className="rounded-sm"
              placeholder={t('invoices.statusChangeReasonPlaceholder')}
              rows={3}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              className="rounded-sm"
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={loading || !newStatus || !reason.trim()}
              className="rounded-sm bg-amber-600 hover:bg-amber-700"
            >
              {loading ? t('common.loading') : t('invoices.confirmStatusChange')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminStatusChangeDialog;
